<template>
	<div
		class="audio-controls"
		aria-label="Volume controls"
	>
		<HYIconButton
			class="audio-controls__mute-button"
			:class="{ 'audio-controls__mute-button--muted': isMuted }"
			variant="deep"
			:disabled="props.isDisabled"
			:icon="isMuted ? 'VolumeOff' : 'VolumeOn'"
			:label="isMuted ? 'Unmute' : 'Mute'"
			size="xs"
			@click="handleClickMuteToggle"
		/>

		<section
			class="audio-controls__volume-container"
			aria-label="Volume level dropdown"
		>
			<div class="audio-controls__volume-slider-container">
				<HYSlider
					id="volume-slider"
					class="audio-controls__volume-slider"
					label="Volume level"
					:showInput="false"
					:showLabel="false"
					:fullWidth="false"
					:value="props.volume * 100"
					:disabled="props.isDisabled"
					@input="handleVolumeChange"
				/>
			</div>
		</section>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { HYIconButton, HYSlider } from '@voicemod1/harmony-ui';

const props = defineProps<{
	isDisabled?: boolean;
	volume: number;
}>();

const emit = defineEmits<{
	onChangeVolume: [volume: number];
	onToggleMute: [];
}>();

const isMuted = computed(() => props.volume === 0);

const handleVolumeChange = (event: Event) => {
	const target = event.target as HTMLInputElement;

	emit('onChangeVolume', Number(target.value) / 100);
};

const handleClickMuteToggle = () => {
	emit('onToggleMute');
};
</script>

<style scoped lang="scss">
.audio-controls {
	display: flex;
	flex-direction: column;
	gap: var(--hy-space-2s);
	align-items: center;
	justify-content: center;

	&__volume-container {
		position: absolute;
		top: 28px;
		left: 0;

		display: flex;
		align-items: center;
		justify-content: center;

		width: 32px;
		height: 30px;
		margin-left: -6px;

		opacity: 0;
		background-color: var(--hy-color-surface-neutral-bold);
		border-radius: var(--hy-space-xs);

		transition: all 0.1s ease-in-out;
	}

	&__volume-slider-container {
		transform: rotate(-90deg);
		scale: 0.5;

		width: 100px;
		height: 6px;

		transition: scale 0.1s ease-in-out;
	}

	&:hover > .audio-controls__volume-container {
		height: 116px;
		opacity: 1;
	}

	&:hover
		> .audio-controls__volume-container
		.audio-controls__volume-slider-container {
		scale: 1;
		opacity: 1;
	}

	&__volume-slider {
		width: 100px;
	}

	&__mute-button {
		border-radius: var(--hy-radius-xs);
		transition: background-color 0.2s ease-in-out;

		&--muted {
			color: black;
			background-color: var(--hy-color-surface-danger-default);

			&:hover {
				background-color: var(--hy-color-surface-danger-hover);
			}
		}
	}
}
</style>
